import { useEffect, useRef, useState, useCallback } from "react";
import { MapPin, Loader2 } from "lucide-react";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_KEY;

// Charge le script Google Maps une seule fois
let googleLoadPromise = null;
const loadGoogleScript = () => {
  if (googleLoadPromise) return googleLoadPromise;
  if (window.google?.maps?.places) return Promise.resolve();

  googleLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places&language=fr`;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error("Google Maps script failed to load"));
    document.head.appendChild(script);
  });
  return googleLoadPromise;
};

export default function AddressAutocomplete({ value, onChange, onSelect, placeholder = "Rechercher une adresse..." }) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);

  // Charger le script Google
  useEffect(() => {
    loadGoogleScript()
      .then(() => { setReady(true); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Initialiser l'autocomplete quand prêt
  useEffect(() => {
    if (!ready || !inputRef.current || autocompleteRef.current) return;

    autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ["address"],
      fields: ["formatted_address", "geometry", "address_components"],
    });

    autocompleteRef.current.addListener("place_changed", () => {
      const place = autocompleteRef.current.getPlace();
      if (!place?.geometry) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const address = place.formatted_address || "";

      onChange?.(address);
      onSelect?.({ address, lat, lng, place });
    });
  }, [ready]);

  // Sync value changes from parent (sans déclencher Google)
  const handleInput = useCallback((e) => {
    onChange?.(e.target.value);
  }, [onChange]);

  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
        {loading ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} />}
      </div>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInput}
        placeholder={placeholder}
        className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
}
